"use client"

import type { CSSProperties, ReactNode } from "react"

import Image from "next/image"
import { useTranslations } from "next-intl"

import { CloseIcon, Heading, IconButton, Row, Sheet, Stack, Text, type HeadingProps } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { tv } from "@/lib/utils/tv"

const MAX_TILES = 3

interface ReferralDialogProps {
  open: boolean
  onClose: () => void
  titleLead: string
  titleEmphasis: string
  imageUrls: readonly string[]
  children: ReactNode
  testId?: string
}

interface ReferralDialogListProps {
  as: "ol" | "ul"
  label?: string
  items: { key: string; lead: ReactNode; text: ReactNode }[]
}

export const accent = (chunks: ReactNode) => <span className="text-fg-brand">{chunks}</span>

export const ReferralDialogList = ({ as, label, items }: ReferralDialogListProps) => (
  <Stack as={as} gap="0" divide className="referral-rise" aria-label={label}>
    {items.map(({ key, lead, text }) => (
      <Row key={key} as="li" gap="300" align="start" className="py-400 first:pt-200">
        {lead}
        <Text as="span" variant="body" family="text" tone="strong">
          {text}
        </Text>
      </Row>
    ))}
  </Stack>
)

export const ReferralBenefits = ({ benefits }: { benefits: { icon: string; text: ReactNode }[] }) => (
  <ReferralDialogList as="ul" items={benefits.map(({ icon, text }) => ({ key: icon, lead: <Image src={icon} alt="" width={24} height={24} className="shrink-0" />, text }))} />
)

interface ReferralTitleProps extends HeadingProps {
  lead: ReactNode
  emphasis: ReactNode
}

export const ReferralTitle = ({ lead, emphasis, ...rest }: ReferralTitleProps) => (
  <Heading family="hero" tone="inverted" balance {...rest}>
    <span className="text-fg-inverted/65 block">{lead}</span>
    <span className="block">{emphasis}</span>
  </Heading>
)

const tile = tv({
  base: "referral-tile-float rounded-7-5x p-125 story-glass",
  variants: { lead: { true: "w-39.5 md:w-32.5", false: "w-27.5 md:w-25" } },
})

const tileOrder = (imageUrls: readonly string[]) => {
  const [lead, ...rest] = imageUrls.slice(0, MAX_TILES)
  if (lead === undefined) return []
  const [left, right] = rest
  return [left, lead, right].flatMap((src, index) => (src === undefined ? [] : [{ src, lead: index === 1 }]))
}

const ProductTiles = ({ imageUrls }: { imageUrls: readonly string[] }) => (
  <Row gap="500" justify="center" className="-mx-500 overflow-x-clip py-200 md:mx-0 md:gap-400" aria-hidden>
    {tileOrder(imageUrls).map(({ src, lead }, index) => (
      <div key={`${index}-${src}`} className="referral-tile shrink-0" style={{ "--tile-order": lead ? 0 : index === 0 ? 1 : 2 } as CSSProperties}>
        <div className={tile({ lead })} style={{ "--float-order": index } as CSSProperties}>
          <div className="rounded-5x bg-surface-paper relative aspect-square overflow-hidden">
            <TileImage src={src} alt="" fill sizes={lead ? "160px" : "112px"} className="object-cover" />
          </div>
        </div>
      </div>
    ))}
  </Row>
)

export const ReferralDialog = ({ open, onClose, titleLead, titleEmphasis, imageUrls, children, testId }: ReferralDialogProps) => {
  const t = useTranslations("common")
  return (
    <Sheet
      open={open}
      onClose={onClose}
      side="story"
      handle
      label={`${titleLead} ${titleEmphasis}`}
      testId={testId}
      hero={
        <>
          <Stack gap="600" className="my-auto pt-1200 md:my-0 md:gap-500 md:pt-0">
            <ReferralTitle variant="referralWide" lead={titleLead} emphasis={titleEmphasis} className="referral-rise text-center md:pr-1200 md:text-left" />
            <ProductTiles imageUrls={imageUrls} />
          </Stack>
          <IconButton
            variant="glass"
            aria-label={t("close")}
            icon={<CloseIcon size={16} />}
            onClick={onClose}
            className="top-safe-offset-4 absolute right-400 size-9 md:top-600 md:right-600"
          />
        </>
      }
    >
      {children}
    </Sheet>
  )
}
